import { Einwohnermeldeamt, BundeslandInfo, BreadcrumbItem } from '../types/einwohnermeldeamt';

// Lazy load the large data file to reduce initial bundle size
let einwohnermeldeaemterCache: Einwohnermeldeamt[] | null = null;

export const getEinwohnermeldeaemter = async (): Promise<Einwohnermeldeamt[]> => {
  if (einwohnermeldeaemterCache) {
    return einwohnermeldeaemterCache;
  }
  const module = await import('../data/einwohnermeldeaemter');
  einwohnermeldeaemterCache = module.einwohnermeldeaemter;
  return einwohnermeldeaemterCache;
};

/**
 * Generate URL-friendly slug from a string
 */
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Get all unique Bundesländer with statistics
 */
export const getBundeslaender = async (): Promise<BundeslandInfo[]> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  const bundeslaenderMap = new Map<string, { count: number; population: number }>();

  einwohnermeldeaemter.forEach(location => {
    const existing = bundeslaenderMap.get(location.bundesland) || { count: 0, population: 0 };
    bundeslaenderMap.set(location.bundesland, {
      count: existing.count + 1,
      population: existing.population + (location.einwohner || 0)
    });
  });

  return Array.from(bundeslaenderMap.entries())
    .map(([name, stats]) => ({
      name,
      slug: generateSlug(name),
      locationCount: stats.count,
      totalPopulation: stats.population
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Get all locations for a specific Bundesland
 */
export const getLocationsByBundesland = async (bundeslandSlug: string): Promise<Einwohnermeldeamt[]> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  return einwohnermeldeaemter
    .filter(loc => loc.bundeslandSlug === bundeslandSlug)
    .sort((a, b) => a.gemeindeName.localeCompare(b.gemeindeName));
};

/**
 * Get a specific location by Bundesland and location slug
 */
export const getLocationBySlug = async (
  bundeslandSlug: string,
  locationSlug: string
): Promise<Einwohnermeldeamt | undefined> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  return einwohnermeldeaemter.find(
    loc => loc.bundeslandSlug === bundeslandSlug && loc.slug === locationSlug
  );
};

/**
 * Get related locations (same Kreis, excluding current location)
 */
export const getRelatedLocations = async (
  currentLocation: Einwohnermeldeamt,
  limit: number = 5
): Promise<Einwohnermeldeamt[]> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  return einwohnermeldeaemter
    .filter(loc => 
      loc.kreis === currentLocation.kreis && 
      loc.slug !== currentLocation.slug
    )
    .slice(0, limit);
};

/**
 * Get related locations with intelligent fallback
 * Priority:
 * 1. Same Kreis (current behavior)
 * 2. Same Bundesland, sorted by population similarity
 * 3. Similar-sized cities across Germany (for city-states or isolated locations)
 */
export const getRelatedLocationsWithFallback = async (
  currentLocation: Einwohnermeldeamt,
  limit: number = 6
): Promise<{ locations: Einwohnermeldeamt[]; strategy: 'same-kreis' | 'same-bundesland' | 'similar-cities' }> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  
  // Strategy 1: Same Kreis
  const sameKreis = einwohnermeldeaemter
    .filter(loc => 
      loc.kreis === currentLocation.kreis && 
      loc.slug !== currentLocation.slug
    )
    .slice(0, limit);
  
  if (sameKreis.length >= 3) {
    return { locations: sameKreis, strategy: 'same-kreis' };
  }
  
  // Strategy 2: Same Bundesland by population similarity
  const sameBundesland = einwohnermeldeaemter
    .filter(loc => 
      loc.bundesland === currentLocation.bundesland && 
      loc.slug !== currentLocation.slug
    )
    .sort((a, b) => {
      // Sort by population similarity
      const diffA = Math.abs((a.einwohner || 0) - (currentLocation.einwohner || 0));
      const diffB = Math.abs((b.einwohner || 0) - (currentLocation.einwohner || 0));
      return diffA - diffB;
    })
    .slice(0, limit);
  
  if (sameBundesland.length > 0) {
    return { locations: sameBundesland, strategy: 'same-bundesland' };
  }
  
  // Strategy 3: Similar-sized cities across Germany (for city-states or isolated locations)
  const similarCities = einwohnermeldeaemter
    .filter(loc => 
      loc.slug !== currentLocation.slug
    )
    .sort((a, b) => {
      // Sort by population similarity
      const diffA = Math.abs((a.einwohner || 0) - (currentLocation.einwohner || 0));
      const diffB = Math.abs((b.einwohner || 0) - (currentLocation.einwohner || 0));
      return diffA - diffB;
    })
    .slice(0, limit);
  
  return { locations: similarCities, strategy: 'similar-cities' };
};

/**
 * Get section title based on strategy
 */
export const getRelatedLocationsSectionTitle = (
  strategy: 'same-kreis' | 'same-bundesland' | 'similar-cities',
  location: Einwohnermeldeamt
): string => {
  switch(strategy) {
    case 'same-kreis':
      return `Weitere Meldeämter im Kreis ${location.kreis}`;
    case 'same-bundesland':
      return `Weitere Meldeämter in ${location.bundesland}`;
    case 'similar-cities':
      return `Weitere Meldeämter in Deutschland`;
  }
};

/**
 * Format number with German thousand separators
 */
export const formatNumber = (num: number | undefined): string => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString('de-DE');
};

/**
 * Generate breadcrumb items for navigation
 */
export const generateBreadcrumbs = async (
  bundeslandSlug?: string,
  locationSlug?: string
): Promise<BreadcrumbItem[]> => {
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Startseite', path: '/' },
    { label: 'Einwohnermeldeämter', path: '/einwohnermeldeamt' }
  ];

  if (bundeslandSlug) {
    const einwohnermeldeaemter = await getEinwohnermeldeaemter();
    const location = einwohnermeldeaemter.find(loc => loc.bundeslandSlug === bundeslandSlug);
    if (location) {
      breadcrumbs.push({
        label: location.bundesland,
        path: `/einwohnermeldeamt/${bundeslandSlug}`
      });

      if (locationSlug) {
        const currentLocation = einwohnermeldeaemter.find(
          loc => loc.bundeslandSlug === bundeslandSlug && loc.slug === locationSlug
        );
        if (currentLocation) {
          breadcrumbs.push({
            label: `Einwohnermeldeamt ${getCleanLocationName(currentLocation.gemeindeName)}`,
            path: `/einwohnermeldeamt/${bundeslandSlug}/${locationSlug}`
          });
        }
      }
    }
  }

  return breadcrumbs;
};

/**
 * Get Bundesland name from slug
 */
export const getBundeslandName = async (slug: string): Promise<string | undefined> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  const location = einwohnermeldeaemter.find(loc => loc.bundeslandSlug === slug);
  return location?.bundesland;
};

/**
 * Search locations by name or PLZ
 */
export const searchLocations = async (query: string): Promise<Einwohnermeldeamt[]> => {
  const einwohnermeldeaemter = await getEinwohnermeldeaemter();
  const normalizedQuery = query.toLowerCase().trim();
  
  return einwohnermeldeaemter.filter(loc =>
    loc.gemeindeName.toLowerCase().includes(normalizedQuery) ||
    (loc.postleitzahl && loc.postleitzahl.includes(normalizedQuery)) ||
    (loc.ort && loc.ort.toLowerCase().includes(normalizedQuery))
  );
};

/**
 * Remove "Gemeinde" prefix and ", Stadt" suffix from location name for cleaner display
 * Must match the cleaning logic in Ortsschild component
 */
export const getCleanLocationName = (gemeindeName: string): string => {
  // Remove prefixes like "Gemeinde", "Stadt", "Kreisstadt", "Große Kreisstadt"
  let clean = gemeindeName.replace(/^(Gemeinde|Stadt|Kreisstadt|Große Kreisstadt)\s+/i, '');
  // Remove suffixes like ", Landeshauptstadt", ", Stadt", ", M", ", St", ", Markt", etc.
  clean = clean.replace(/,\s*(Landeshauptstadt|Stadt|Freie und Hansestadt|Kreisfreie Stadt|Hansestadt|Universitätsstadt|Residenzstadt|Kreisstadt|Große Kreisstadt|Bergringstadt|Warbelstadt|Barlachstadt|Konfirmationsstadt|Brüder-Grimm-Stadt|Einhardstadt|Liebenbachstadt|Hochschulstadt|documenta-Stadt|Klingenstadt|Kupferstadt|Solestadt|Goethestadt|Welterbestadt|Schliemannstadt|Peenestadt|Vier-Tore-Stadt|Inselstadt|Ostseebad|Seebad|Nordseebad|Ostseeheilbad|Seegemeinde|Klostergemeinde|Point-Alpha-Gemeinde|Nationalparkgemeinde|Nationalparkstadt|gemfr\. Bezirk|Blumenstadt|Windmühlenstadt|Hugenotten- und Waldenserstadt|Reformationsstadt|Schöfferstadt|Barbarossastadt|Bernsteinstadt|Hanse- und Universitätsstadt|Universitäts- und Hansestadt|Flecken|M|St|Markt|Marktgemeinde|Marktflecken).*$/i, '');
  return clean.trim();
};

